import { create } from 'zustand';
import type { GroupAnalysis, HolderRow, TerminalTrade, WalletGroup } from '@pleco/shared';
import { groupTint } from '@pleco/shared';
import { terminalApi, type Timeframe, type TradeSyncStatus } from '../lib/terminalApi.js';
import { API_BASE } from '../lib/api.js';

/** How new trades reach this page: streamed (~1-2 s) or polled (~6 s+). */
export interface LiveLink {
  connected: boolean;
  mode: 'stream' | 'poll';
  reason?: string;
}

/**
 * State of the trading terminal for one token. Selection is shared by the
 * bubble map, chart, trades feed, holders table and groups panel, so
 * picking a group or wallet anywhere highlights it everywhere.
 */

export type RightTab = 'trades' | 'holders' | 'groups' | 'risk';
export type ColorMode = 'groups' | 'clusters';

interface TerminalState {
  mint?: string;
  status?: TradeSyncStatus;
  trades: TerminalTrade[];
  tradesError?: string;
  analysis?: GroupAnalysis;
  holders: HolderRow[];
  priceSol?: number;
  solUsd?: number;
  live: LiveLink;

  tab: RightTab;
  colorMode: ColorMode;
  tf: Timeframe;
  tfAuto: boolean;
  chartMode: 'price' | 'mcap';
  selectedGroupId?: string;
  hoverWallet?: string;
  /** Wallet drawer navigation stack (fund-flow re-centering pushes). */
  drawer: string[];

  init: (mint: string) => void;
  dispose: () => void;
  refreshTrades: () => Promise<void>;
  refreshGroups: () => Promise<void>;
  refreshHolders: () => Promise<void>;
  setTab: (t: RightTab) => void;
  setColorMode: (m: ColorMode) => void;
  setTf: (tf: Timeframe) => void;
  /** Pick a timeframe automatically once, unless the user already chose one. */
  autoTf: (tf: Timeframe) => void;
  setChartMode: (m: 'price' | 'mcap') => void;
  selectGroup: (id?: string) => void;
  setHoverWallet: (a?: string) => void;
  openWallet: (a: string) => void;
  pushWallet: (a: string) => void;
  backWallet: () => void;
  closeWallet: () => void;
}

let timers: ReturnType<typeof setTimeout>[] = [];
let generation = 0;
let source: EventSource | undefined;

/**
 * Merge new trades into the feed: newest first, deduped, capped. Group
 * tracing runs in the background, so a trade's tags/groupId can be briefly
 * absent on one fetch and present on the next (or vice versa, right after a
 * regroup clears the cache): never let a trade lose tag info it already had.
 */
function mergeTrades(cur: TerminalTrade[], incoming: TerminalTrade[]): TerminalTrade[] {
  const prior = new Map(cur.map((t) => [`${t.signature}:${t.idx}`, t]));
  const inSigs = new Set(incoming.map((t) => `${t.signature}:${t.idx}`));
  // the stream and the poller can index one transaction differently: one row per signature+trader
  const inTx = new Set(incoming.map((t) => `${t.signature}:${t.trader}`));
  return incoming
    .map((t) => {
      const old = prior.get(`${t.signature}:${t.idx}`);
      return old ? { ...t, groupId: t.groupId ?? old.groupId, tags: t.tags?.length ? t.tags : old.tags } : t;
    })
    .concat(cur.filter((t) => !inSigs.has(`${t.signature}:${t.idx}`) && !inTx.has(`${t.signature}:${t.trader}`)))
    .sort((a, b) => b.ts - a.ts || b.slot - a.slot)
    .slice(0, MAX_FEED);
}

const MAX_FEED = 400;

export const useTerminal = create<TerminalState>((set, get) => ({
  trades: [],
  holders: [],
  tab: 'trades',
  colorMode: 'groups',
  tf: '15s',
  tfAuto: true,
  chartMode: 'mcap',
  drawer: [],
  live: { connected: false, mode: 'poll' },

  init(mint) {
    get().dispose();
    const gen = ++generation;
    set({
      mint,
      trades: [],
      holders: [],
      analysis: undefined,
      status: undefined,
      tradesError: undefined,
      selectedGroupId: undefined,
      drawer: [],
      tfAuto: true,
      live: { connected: false, mode: 'poll' },
    });

    // Live push: new trades arrive the moment the server has them.
    if (typeof EventSource !== 'undefined') {
      const es = new EventSource(`${API_BASE}/v1/sol/${encodeURIComponent(mint)}/live`, { withCredentials: true });
      source = es;
      es.addEventListener('state', (e) => {
        if (gen !== generation) return;
        const s = JSON.parse((e as MessageEvent).data) as { mode: 'stream' | 'poll'; reason?: string };
        set({ live: { connected: true, mode: s.mode, reason: s.reason } });
      });
      es.addEventListener('trades', (e) => {
        if (gen !== generation) return;
        const { trades } = JSON.parse((e as MessageEvent).data) as { trades: TerminalTrade[] };
        const a = get().analysis;
        const tagged = trades.map((t) => {
          const w = a?.wallets[t.trader];
          return w && !t.groupId ? { ...t, groupId: w.groupId, tags: w.tags } : t;
        });
        set({ trades: mergeTrades(get().trades, tagged) });
      });
      es.onerror = () => {
        // EventSource reconnects by itself; poll faster meanwhile
        if (gen === generation) set({ live: { ...get().live, connected: false } });
      };
    }

    // Self-rescheduling pollers; a newer init() (another token) stops them.
    const loop = (fn: () => Promise<void>, every: () => number) => {
      const tick = async () => {
        if (gen !== generation) return;
        await fn().catch(() => undefined);
        if (gen !== generation) return;
        timers.push(setTimeout(tick, every()));
      };
      void tick();
    };
    // With the live push connected, polling only reconciles (no upstream sync).
    loop(() => get().refreshTrades(), () => (get().live.connected ? 20_000 : document.hidden ? 20_000 : 5_000));
    loop(() => get().refreshGroups(), () => (get().analysis?.progress.tracing ? 6_000 : 30_000));
    loop(() => get().refreshHolders(), () => 30_000);
  },

  dispose() {
    generation++;
    source?.close();
    source = undefined;
    for (const t of timers) clearTimeout(t);
    timers = [];
  },

  async refreshTrades() {
    const mint = get().mint;
    if (!mint) return;
    try {
      // live push connected: read what the server has, don't make it sync
      const r = await terminalApi.trades(mint, { limit: 200, sync: get().live.connected ? false : undefined });
      if (get().mint !== mint) return;
      set({ trades: mergeTrades(get().trades, r.trades), status: r.status, tradesError: undefined });
    } catch (e) {
      set({ tradesError: e instanceof Error ? e.message : 'Trades unavailable' });
      throw e;
    }
  },

  async refreshGroups() {
    const mint = get().mint;
    if (!mint) return;
    const analysis = await terminalApi.groups(mint);
    if (get().mint !== mint) return;
    // Re-tag trades already in the feed with the newest group info.
    const trades = get().trades.map((t) => {
      const w = analysis.wallets[t.trader];
      return w ? { ...t, groupId: w.groupId, tags: w.tags } : t;
    });
    set({ analysis, trades });
  },

  async refreshHolders() {
    const mint = get().mint;
    if (!mint) return;
    const r = await terminalApi.holders(mint, 200);
    if (get().mint !== mint) return;
    set({ holders: r.holders, priceSol: r.priceSol, solUsd: r.solUsd });
  },

  setTab: (tab) => set({ tab }),
  setColorMode: (colorMode) => set({ colorMode }),
  setTf: (tf) => set({ tf, tfAuto: false }),
  autoTf: (tf) => set((s) => (s.tfAuto ? { tf } : {})),
  setChartMode: (chartMode) => set({ chartMode }),
  selectGroup: (id) => set((s) => ({ selectedGroupId: s.selectedGroupId === id ? undefined : id })),
  setHoverWallet: (hoverWallet) => set({ hoverWallet }),
  openWallet: (a) => set({ drawer: [a] }),
  pushWallet: (a) => set((s) => (s.drawer.at(-1) === a ? s : { drawer: [...s.drawer, a].slice(-20) })),
  backWallet: () => set((s) => ({ drawer: s.drawer.slice(0, -1) })),
  closeWallet: () => set({ drawer: [] }),
}));

/* ---------------------------------------------------------------- helpers */

export function groupById(analysis: GroupAnalysis | undefined, id: string | undefined): WalletGroup | undefined {
  return id ? analysis?.groups.find((g) => g.id === id) : undefined;
}

export function colorOfGroup(analysis: GroupAnalysis | undefined, id: string | undefined): string | undefined {
  const g = groupById(analysis, id);
  return g ? groupTint(g) : undefined;
}

/** Short label "G3" used on chart markers and badges. */
export const groupLabel = (g: WalletGroup) => `G${g.color + 1}`;
